import { RequiredError } from "../error/RequiredError.js";
import { ValueError } from "../error/ValueError.js";
import { omitItems, pickItems } from "./iterate.js";
import { formatRange } from "./number.js";
/** Is an unknown value an array? */
export function isArray(value) {
    return Array.isArray(value);
}
/** Assert that an unknown value is an array. */
export function assertArray(arr) {
    if (!isArray(arr))
        throw new ValueError(`Must be array`, arr);
}
/** Is an unknown value an item in a specified array? */
export function isArrayItem(arr, item) {
    return arr.includes(item);
}
/** Assert that an unknown value is an item in a specified array. */
export function assertArrayItem(arr, item) {
    if (!isArrayItem(arr, item))
        throw new ValueError(`Must be array item`, item);
}
/** Convert an iterable to an array (if its not already an array). */
export function getArray(items) {
    return isArray(items) ? items : Array.from(items);
}
/** Add multiple items to an array (immutably) and return a new array with those items (or the same array if no changes were made). */
export function withArrayItems(arr, ...items) {
    const extras = items.filter(_doesNotInclude, arr);
    return extras.length ? [...arr, ...extras] : arr;
}
function _doesNotInclude(value) {
    return !this.includes(value);
}
/** Pick multiple items from an array (immutably) and return a new array without those items (or the same array if no changes were made). */
export function pickArrayItems(input, ...pick) {
    const output = Array.from(pickItems(input, ...pick));
    return isArray(input) && output.length === input.length ? input : output;
}
/** Remove multiple items from an array (immutably) and return a new array without those items (or the same array if no changes were made). */
export function omitArrayItems(input, ...omit) {
    const output = Array.from(omitItems(input, ...omit));
    return isArray(input) && output.length === input.length ? input : output;
}
/** Toggle an item in and out of an array (immutably) and return a new array with or without the specified items (or the same array if no changes were made). */
export function toggleArrayItems(input, ...items) {
    const extras = items.filter(_doesNotInclude, input);
    const output = input.filter(_doesNotInclude, items);
    return extras.length ? [...output, ...extras] : output.length !== input.length ? output : input;
}
/** Get the first item from an array or iterable, or `undefined` if it didn't exist. */
export function getOptionalFirstItem(items) {
    const arr = getArray(items);
    return 0 in arr ? arr[0] : undefined;
}
/** Get the first item from an array or iterable. */
export function getFirstItem(items) {
    const item = getOptionalFirstItem(items);
    if (item === undefined)
        throw new RequiredError("First item is required");
    return item;
}
/** Get the last item from an array or iterable, or `undefined` if it didn't exist. */
export function getOptionalLastItem(items) {
    const arr = getArray(items);
    const j = arr.length - 1;
    if (j in arr)
        return arr[j];
}
/** Get the last item from an array or iterable. */
export function getLastItem(items) {
    const item = getOptionalLastItem(items);
    if (item === undefined)
        throw new RequiredError("Last item is required");
    return item;
}
/** Get the next item in an array or iterable. */
export function getOptionalNextItem(items, value) {
    const arr = getArray(items);
    const i = arr.indexOf(value);
    if (i >= 0) {
        const j = i + 1;
        if (j in arr)
            return arr[j];
    }
}
/** Get the previous item in an array or iterable. */
export function getOptionalPrevItem(items, value) {
    const arr = getArray(items);
    const i = arr.indexOf(value);
    if (i >= 1) {
        const j = i - 1;
        if (j in arr)
            return arr[j];
    }
}
/** Return a shuffled version of an array or iterable. */
export function shuffleArray(input) {
    const output = Array.from(input);
    for (let i = output.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [output[i], output[j]] = [output[j], output[i]]; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    }
    return output;
}
/** Add an item to an array (by reference) and return the set item. */
export function addArrayItem(arr, item) {
    if (arr.indexOf(item) < 0)
        arr.push(item);
    return item;
}
/** Add multiple items to an array (by reference). */
export function addArrayItems(arr, ...items) {
    for (const item of items)
        if (arr.indexOf(item) < 0)
            arr.push(item);
}
/** Remove multiple items from an array (by reference). */
export function deleteArrayItems(arr, ...items) {
    for (let i = arr.length - 1; i >= 0; i--)
        if (i in arr && items.includes(arr[i]))
            arr.splice(i, 1);
}
/** Return an array of the unique items in an array. */
export function getUniqueArray(input) {
    const output = [];
    for (const item of input)
        if (!output.includes(item))
            output.push(item);
    return isArray(input) && input.length === output.length ? input : output;
}
/** Apply a limit to an array. */
export function limitArray(items, limit) {
    const arr = getArray(items);
    return limit > arr.length ? arr : arr.slice(0, limit);
}
/** Count the items in an array. */
export function countArray(arr) {
    return arr.length;
}
export function isArrayLength(arr, min = 1, max = Infinity) {
    return arr.length >= min && arr.length <= max;
}
export function assertArrayLength(arr, min = 1, max = Infinity) {
    if (!isArray(arr) || !isArrayLength(arr, min, max))
        throw new ValueError(`Must be array with length ${formatRange(min, max)}`, arr);
}
export function getArrayLength(arr, min = 1, max = Infinity) {
    assertArrayLength(arr, min, max);
    return arr;
}
